import * as msalConfig from './config/msal.config';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { GridModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { MenusModule } from '@progress/kendo-angular-menu';
import { EditorModule } from "@progress/kendo-angular-editor"; 
import { NgModule } from '@angular/core';
import { WinAuthInterceptor } from './services/WinAuthInterceptor';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { WidgetResidualCalculationService } from './services/WidgetResidualCalculationService';
import { AppCommonModule } from './components/common/app-common.module';
import { ReportDesignerModule } from './modules/report-designer/report-designer.module';
import * as oktaConfig from './config/okta.config';
import { OktaAuthGuard, OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaInterceptor } from './services/OktaInterceptor';
import { environment } from '../environments/environment';
import { ProjectValuationModule } from './modules/project-valuation/project-valuation.module';
import { PopupModule } from '@progress/kendo-angular-popup';
import { ApiKeyInterceptor } from './services/ApiKeyInterceptor';
import { AppSettingsModule } from './modules/app-settings/app-settings.module';
import { RaciModule } from './modules/raci/raci.module';
import { NavigationModule } from './navigation/navigation.module';
import { ArgusIntegrationService } from './services/ArgusIntegrationService';
import { NgxPendoModule } from 'ngx-pendo';

/******************************************************
 * LOCALES IMPORT START
 ******************************************************/
import { registerLocaleData } from '@angular/common';

/*
  en-US is registred by default (will be used for decimalSeparator '.')
  we will register de-DE too, to use with decimalSeparator ','
  */
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { TableSettingsService } from './services/TableSettingsService';
import { ComparablesModule } from './modules/comparables/comparables.module';
import { AppLocalizationService } from './services/AppLocalizationService';
registerLocaleData(localeDe, 'de-DE', localeDeExtra);

/******************************************************
 * LOCALES IMPORT END
 ******************************************************/

import "hammerjs"; //For Kendo charts

/**
 * The window
 */
const _window: any = window;

/**
 * Check if ApiKey is used
 */
const apiKeyInfo = _window.apiKeyInfo;

/**
 * Authentication providers
 */
let authProviders: Array<any> = [];

/**
 * Authentication modules
 */
let authModules: Array<any> = [];
if (apiKeyInfo) {
  authProviders = authProviders.concat([
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiKeyInterceptor,
      multi: true
    }
  ]);
} else if (environment.UseMsal) {
  authProviders = authProviders.concat([
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalConfig.MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalConfig.MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalConfig.MSALInterceptorConfigFactory,
    },
    MsalBroadcastService,
    MsalService,
    MsalGuard
  ]);

  authModules = [MsalModule];

} else if (environment.UseOkta) {
  authProviders = authProviders.concat([
    {
      provide: OKTA_CONFIG,
      useFactory: oktaConfig.OktaConfigFactory
    },
    { provide: HTTP_INTERCEPTORS, useClass: OktaInterceptor, multi: true },
    OktaAuthGuard
  ]);

  authModules = [OktaAuthModule];

} else {
  authProviders = authProviders.concat([
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WinAuthInterceptor,
      multi: true
    },
  ]);
}

/**
 * AoT requires an exported function for factories
 * @param http HttpClient
 */
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/translation/lang-', '.json');
}

/**
 * Translate module
 */
export const translateModule = TranslateModule.forRoot({
  loader: {
    provide: TranslateLoader,
    useFactory: (createTranslateLoader),
    deps: [HttpClient]
  }
});

/**
 * Pendo module
 */
let pendoModules: Array<any> = [];
if (environment.PendoApiKey) {
  const hash = _window.location.hash;
  const isPrintRequest = hash.indexOf('#/rd') === 0 && hash.indexOf('PdfExportId') !== -1;
  if (!isPrintRequest) {
    pendoModules = [
      NgxPendoModule.forRoot({
        pendoApiKey: environment.PendoApiKey,
        pendoScriptOrigin: environment.PendoScriptOrigin,
        pendoIdFormatter: (value: any) => value.toString().toLowerCase()
      })
    ];
  }
}

/**
 * App main module
 */
@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    InputsModule,
    EditorModule,
    BrowserAnimationsModule,
    LayoutModule,
    ButtonsModule,
    GridModule,
    MenusModule,
    DateInputsModule,
    ChartsModule,
    translateModule,
    DropDownsModule,
    ExcelExportModule,
    AppCommonModule,
    ReportDesignerModule,
    ProjectValuationModule,
    ComparablesModule,
    PopupModule,
    AppSettingsModule,
    RaciModule,
    NavigationModule
  ].concat(pendoModules).concat(authModules),
  providers: [
    { provide: '$scope', useExisting: '$rootScope' },
    ArgusIntegrationService,
    WidgetResidualCalculationService,
    TableSettingsService,
    AppLocalizationService
  ].concat(authProviders),
  bootstrap: [AppComponent]
})
export class AppModule {
  ngDoBootstrap() {
    console.log("ngDoBootstrap entry");
  }
}
